import * as Plot from "@observablehq/plot";
import React from "react";
import {bus, cat, cos, Expense, expenseCategories, getExpenseCatTextFromSN, pri, Pricing, proServ} from "../../../firebase/index.js";


export function createPricingPlots(pricings: Pricing[]): ((SVGSVGElement & Plot) | (HTMLElement & Plot))[] {
	  
	  try {
			 
			 const mainSortedDescGraphArray = [...pricings]
						.sort((a, b) => {
							  const u = a.unit.localeCompare(b.unit);
							  if (u !== 0) return u;
							  const q = a.quantity - b.quantity;
							  if (q !== 0) return q;
							  return a.price - b.price;
						}).reverse()
						.map((p) => {
							  return {
									 bn: p.businessName,
									 pn: p.productName,
									 pvn: p.productVariantName,
									 pfn: p.productVariantName + " " + p.productName,
									 price: p.price,
									 unit: p.unit,
									 quantity: p.quantity
							  };
						});
			 
			 
			 const maxPrice = mainSortedDescGraphArray.reduce((acc, cur) => acc > cur.price ? acc : cur.price, 0);
			 const minPrice = mainSortedDescGraphArray.reduce((acc, cur) => acc < cur.price ? acc : cur.price, maxPrice);
			 
			 const eachHeight = 20;
			 const margin = 50;
			 const width = window.innerWidth - (margin * 2);
			 const createHeight = (array: any[]) => (array.length * eachHeight) + (margin * 2);
			 
			 const step = 10;
			 const segmentsForX = Array((maxPrice / step) + 1).fill().map((value, index) => step * index);
			 
			 // BUSINESS-WISE PRICING OF PRODUCTS/SERVICES
			 const plotA = Plot.plot({
					// color: {legend: true},
					marks: [
						  Plot.barX(mainSortedDescGraphArray, {fy: "bn", x: "price", y: "pfn", inset: 0.25, fill: "pfn"}),
						  Plot.ruleX(segmentsForX, {opacity: 0.5}),
						  Plot.ruleX([0, minPrice, maxPrice])
					],
					margin,
					marginLeft: margin * 4,
					marginRight: margin * 4,
					height: createHeight(mainSortedDescGraphArray),
					width,
					x: {
						  label: pri, labelAnchor: "center", labelArrow: false
					},
					y: {
						  label: proServ, labelAnchor: "center"
					},
					fy: {
						  label: bus, labelAnchor: "center"
					}
			 });
			 
			 // MOST EXPENSIVE TO THE LEAST EXPENSIVE PRODUCTS/SERVICES
			 const graphArrayPlotB = mainSortedDescGraphArray.filter((p, index, self) => self.findIndex((t) => t.pfn === p.pfn) === index);
			 
			 const plotB = Plot.plot({
					// color: {legend: true},
					marks: [
						  Plot.barX(graphArrayPlotB, {x: "price", y: "pfn", inset: 0.25, fill: "pfn", sort: {y: "-x"}}),
						  Plot.ruleX(segmentsForX,{opacity:0.5}),
						  Plot.ruleX([0, maxPrice])
					],
					margin,
					marginLeft: margin * 4,
					marginRight: margin * 4,
					height: createHeight(graphArrayPlotB),
					width,
					x: {
						  label: pri, labelAnchor: "center", labelArrow: false
					},
					y: {
						  label: proServ, labelAnchor: "center"
					}
			 });
			 
			 // MOST EXPENSIVE TO THE LEAST EXPENSIVE BUSINESSES
			 const graphArrayPlotC = mainSortedDescGraphArray.filter((p, index, self) => self.findIndex((t) => t.bn === p.bn) === index);
			 
			 const plotC = Plot.plot({
					// color: {legend: true},
					marks: [
						  Plot.barX(graphArrayPlotC, {x: "price", y: "bn", inset: 0.25, fill: "bn", sort: {y: "-x"}}),
						  Plot.ruleX(segmentsForX,{opacity:0.5}),
						  Plot.ruleX([0, maxPrice])
					],
					margin,
					marginLeft: margin * 4,
					marginRight: margin * 4,
					height: createHeight(graphArrayPlotC),
					width,
					x: {
						  label: pri, labelAnchor: "center", labelArrow: false
					},
					y: {
						  label: bus, labelAnchor: "center"
					}
			 });
			 
			 // PRODUCT-WISE PRICING IN BUSINESSES
			 const plotD = Plot.plot({
					// color: {legend: true},
					marks: [
						  Plot.barX(mainSortedDescGraphArray, {fy: "pfn", x: "price", y: "bn", inset: 0.25, fill: "bn"}),
						  Plot.ruleX(segmentsForX,{opacity:0.5}),
						  Plot.ruleX([0, maxPrice])
					],
					margin,
					marginLeft: margin * 4,
					marginRight: margin * 4,
					height: createHeight(mainSortedDescGraphArray),
					width,
					x: {
						  label: pri, labelAnchor: "center", labelArrow: false
					},
					y: {
						  label: bus, labelAnchor: "center"
					},
					fy: {
						  label: proServ, labelAnchor: "center"
					}
			 });
			 
			 
			 return [plotB, plotC, plotA, plotD];
			 
	  } catch (e) {
			 console.error(e);
	  }
	  
	  return [];
}


export function createExpensePlots(expenses: Expense[]): ((SVGSVGElement & Plot) | (HTMLElement & Plot))[] {
	  
	  try {
			 
			 const mainSortedAscGraphArray = [...expenses]; /* THIS IS ASCENDING ORDER FROM THE OLDEST DATE TO THE NEWEST DATE */
			 
			 const maxCost = mainSortedAscGraphArray.reduce((max, exp) => Math.max(max, exp.expenseCost), 0);
			 
			 const eachHeight = 20;
			 const margin = 50;
			 const width = window.innerWidth - (margin * 2);
			 const createHeight = (array: any[]) => (array.length * eachHeight) + (margin * 2);
			 
			 const rangeColours = [
					"#FF0000", // Vivid Red
					"#00FF00", // Vivid Green
					"#0000FF", // Vivid Blue
					"#FFFF00", // Bright Yellow
					"#FF00FF", // Vivid Magenta
					"#00FFFF", // Bright Cyan
					"#FFA500", // Bright Orange
					"#800080", // Deep Purple
					"#008000", // Deep Green
					"#000080", // Deep Blue
					"#FFD700", // Bright Gold
					"#FF4500", // Bright Orange-Red
					"#32CD32", // Lime Green
					"#1E90FF", // Dodger Blue
					"#FF1493", // Deep Pink
					"#8A2BE2", // Blue Violet
					"#FF6347", // Tomato
					"#40E0D0"  // Turquoise
			 ];
			 
			 const step = 2000;
			 const segmentsForX = Array((maxCost / step) + 1).fill().map((value, index) => step * index);
			 
			 // MOST EXPENSIVE TO THE LEAST EXPENSIVE CATEGORIES
			 const graphArrayPlotA = [
					...[...mainSortedAscGraphArray].map((value) => ({co: value.expenseCost, ca: getExpenseCatTextFromSN(value.expenseSerialNumber)})),
					...Object.values(expenseCategories).map((value) => ({co: 0, ca: value.text}))
			 ].filter((value, index, array) => index === array.findIndex((v) => v.ca === value.ca));
			 
			 const plotA = Plot.plot({
					color: {range: rangeColours},
					marks: [
						  Plot.barX(graphArrayPlotA, {y: "ca", x: "co", inset: 0.25, fill: "ca", sort: {y: "-x"}}),
						  Plot.ruleX(segmentsForX, {opacity: 0.5}),
						  Plot.ruleX([0, maxCost])
					],
					margin,
					marginLeft: margin * 6,
					marginRight: margin * 2,
					height: createHeight(graphArrayPlotA),
					width,
					x: {
						  label: cos, labelAnchor: "center", labelArrow: false
					},
					y: {
						  label: cat, labelAnchor: "center"
					}
			 });
			 
			 const get1stOfMo = (millis: number): Date => new Date(new Date(millis).setDate(1));
			 
			 const graphArrayPlotBC = [
					...[...mainSortedAscGraphArray]
							  .map((value) => ({mo: get1stOfMo(value.expenseMillis), co: value.expenseCost, ca: getExpenseCatTextFromSN(value.expenseSerialNumber)})),
					...Object.values(expenseCategories).map((value) => ({mo: get1stOfMo(mainSortedAscGraphArray[0].expenseMillis), co: 0, ca: value.text}))
			 ].filter((value, index, array) => index === array.findIndex((v) => v.ca === value.ca));
			 
			 const months = mainSortedAscGraphArray.filter((value, index, array) => index === array.findIndex((v) => new Date(v.expenseMillis).setDate(1) === new Date(value.expenseMillis).setDate(1)));
			 
			 const maxTotal = Object.values(
						mainSortedAscGraphArray.reduce((accumulator, currentValue) => {
							  const key = get1stOfMo(currentValue.expenseMillis).toLocaleDateString();
							  if (/*FALSY->*/ !!!accumulator[key]) {
									 accumulator[key] = 0;
							  }
							  accumulator[key] += currentValue.expenseCost;
							  return accumulator;
						}, {})
			 ).reduce((p, c) => Math.max(p, c), 0);
			 
			 // MOST EXPENSIVE TO THE LEAST EXPENSIVE IN EACH MONTH
			 const plotB = Plot.plot({
					color: {range: rangeColours},
					marks: [
						  Plot.barX(graphArrayPlotBC, {fy: "mo", y: "ca", x: "co", inset: 0.25, fill: "ca"}),
						  Plot.ruleX(segmentsForX, {opacity: 0.5}),
						  Plot.ruleX([0, maxCost])
					],
					margin,
					marginLeft: margin * 6,
					marginRight: margin * 2,
					height: createHeight(Array(graphArrayPlotBC.length * months.length)), /* FOR PROPER SIZING ONLY */
					width,
					x: {
						  label: cos, labelAnchor: "center", labelArrow: false
					},
					y: {
						  label: cat, labelAnchor: "center"
					},
					fy: {
						  label: "Month", labelAnchor: "center", reverse: true /* IN CASE OF fy/fx, THIS IS NEEDED. OTHERWISE sort:{} IS ENOUGH IF USING ONLY y AND x. */
					}
			 });
			 
			 // MONTH-WISE TOTAL EXPENSES
			 const plotC = Plot.plot({
					color: {legend: true, range: rangeColours},
					marks: [
						  Plot.barX(graphArrayPlotBC, {y: "mo", x: "co", inset: 0.75, fill: "ca", sort: {y: "-y"}}),
						  Plot.ruleX(segmentsForX, {opacity: 0.5}),
						  Plot.ruleX([0, maxTotal])
					],
					margin,
					marginLeft: margin * 2,
					marginRight: margin * 2,
					height: createHeight(months), /* FOR PROPER SIZING ONLY */
					width,
					x: {
						  label: cos, labelAnchor: "center", labelArrow: false
					},
					y: {
						  label: "Month", labelAnchor: "center", type: "band"
					}
			 });
			 
			 return [plotA, plotC, plotB];
			 
	  } catch (e) {
			 console.error(e);
	  }
	  
	  return [];
}
